import React, {useState} from 'react';
import {Typography, Stack, Card, Grid, useTheme} from '@mui/material';
import {Controller, useForm} from 'react-hook-form';
import {TextFieldWrapper, FormHelperTextP} from './FeedbackComponentStyles';
import {getSearchForm} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {useSnackbar} from 'notistack';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {yupResolver} from '@hookform/resolvers/yup';
import {feedbackSchema} from '@shared/modules/public/forms/validation/FeedbackValidations';
import {useTranslation} from 'react-i18next';
import EncryptUtils from 'src/utils/EncryptUtils';
import {feedbackService} from '@shared/services/service-handlers/PublicService';
import TextInput from '@components/form/text-input/text-input/TextInput';
import {useAppContext} from '@context/AppContext';
import {LoadingButton} from '@mui/lab';
import FormProvider from 'src/minimal-components/hook-form/form-provider';
import CustomBreadcrumbs from 'src/minimal-components/custom-breadcrumbs';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import Editor from 'src/minimal-components/editor/editor';

const FeedbackComponent = (props: any): React.ReactElement => {
  const theme = useTheme();
  const {
    appState: {applicationName}
  } = useAppContext();

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [uiFieldsData, setUIFieldsData]: any = useState({});
  const [loader, setLoader] = React.useState(false);
  const [descriptionError, setDescriptionError] = useState<any>(null);
  const {
    handleSubmit,
    control,
    formState: {errors}
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(feedbackSchema)
  });
  const methods = useForm({});

  const init = async () => {
    try {
      if (props.module) {
        setLoader(true);
        const res = await getSearchForm(applicationName, props.module);
        setUIFieldsData(res.searchForm?.fields);
        setLoader(false);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setLoader(false);
    }
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFeedbackSubmit = async (data: any) => {
    if (
      !uiFieldsData.description?.value ||
      uiFieldsData.description?.value?.length < 12
    ) {
      return setDescriptionError(
        'Please enter a description of minimum 5 characters'
      );
    }
    try {
      setLoader(true);
      const feedbackObj = {
        subject: data.subject,
        description: uiFieldsData?.description?.value
      };
      const encryptRequestData = EncryptUtils.encryptedData(feedbackObj);
      const res = await feedbackService({
        tk: encryptRequestData
      });
      const resObj = JSON.parse(JSON.stringify(res));
      const {message, error} = resObj;

      if (!error) {
        enqueueSnackbar(message, SnackBarConfig.getSuccess());
      }
      setLoader(false);
    } catch (error) {
      setLoader(false);
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  const handleTextChange = (value: string, key: string) => {
    setUIFieldsData((prevState: any) => {
      return {
        ...prevState,
        [key]: {
          ...prevState[key],
          value: value
        }
      };
    });
  };

  const renderFeedbackForm = (fields: any, control: any, errors: any) => {
    const textFields: React.ReactNode[] = [];
    for (const key in fields) {
      if (fields[key].type === 'text') {
        textFields.push(
          <TextFieldWrapper>
            <Typography
              sx={{color: 'black', fontWeight: 'bold', fontSize: '5rem'}}
              gutterBottom>
              {fields[key]?.heading[0]}
            </Typography>
            {fields[key].richText ? (
              <>
                <Editor
                  id="minimal-quill"
                  key={fields[key].name}
                  {...fields[key]}
                  value={fields[key].value || ''}
                  onChange={value => {
                    handleTextChange(value, key);
                    setDescriptionError(null);
                  }}
                />
                {descriptionError && (
                  <FormHelperTextP>{descriptionError}</FormHelperTextP>
                )}
              </>
            ) : (
              <Controller
                key={fields[key].name}
                name={fields[key].name}
                control={control}
                render={({field, fieldState: {error}}) => {
                  return (
                    <TextInput
                      variant="outlined"
                      id={fields[key].label}
                      label={t(fields[key].placeholder, {
                        ns: fields[key].labelNS
                      })}
                      name={fields[key].name}
                      error={!!errors[fields[key].name]}
                      helperText={errors?.[key]?.message}
                      keyPressCallBack={(e: any) =>
                        handleTextChange(e.target.value, key)
                      }
                      OnMousePDownCallBack={(e: any) => e.preventDefault()}
                      field={field}
                    />
                  );
                }}
              />
            )}
          </TextFieldWrapper>
        );
      }
    }
    return <div>{textFields}</div>;
  };

  return !loader && uiFieldsData && Object.keys(uiFieldsData).length ? (
    <Stack spacing={3} px={3}>
      <CustomBreadcrumbs
        heading=""
        links={[{name: 'Feedback', href: ''}, {name: 'Submit Feedback'}]}
        sx={{
          mt: 2
        }}
      />
      <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px'}}>
        <FormProvider
          methods={methods}
          onSubmit={handleSubmit(onFeedbackSubmit)}>
          {renderFeedbackForm(uiFieldsData, control, errors)}
          <Stack spacing={3} alignItems="flex-end" sx={{mt: 1}}>
            <Grid item xs={12} display="flex" sm={6} md={12}>
              <LoadingButton
                type="button"
                variant="outlined"
                sx={{
                  mr: 2,
                  bgcolor: theme.palette.primary.light
                }}>
                {t('PRIVATE.ROLES.CANCEL', {ns: 'private'})}
              </LoadingButton>
              <LoadingButton
                type="submit"
                variant="contained"
                sx={{
                  bgcolor: theme.palette.primary.dark
                }}>
                {t('PUBLIC.FEEDBACK.SUBMIT', {ns: 'public'})}
              </LoadingButton>
            </Grid>
          </Stack>
        </FormProvider>
      </Card>
    </Stack>
  ) : (
    <Stack
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      minHeight={'80vh'}>
      <LoadingScreen />
    </Stack>
  );
};

export default FeedbackComponent;
